import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { colors, radius, typography } from "../utils/designTokens";

/**
 * Dropdown propio (no el <select> nativo, que el navegador dibuja descolocado y
 * no se puede estilar). Control con altura consistente + panel alineado debajo,
 * al ancho del control. Uso: <AppSelect value onChange options=[{value,label}] />.
 */
export default function AppSelect({ value, onChange, options = [], placeholder = "Seleccionar", ariaLabel, disabled, style, height = 42 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const sel = options.find((o) => String(o.value) === String(value));
  const label = sel ? sel.label : placeholder;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", ...style }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", height, padding: "0 12px", boxSizing: "border-box",
          border: `1px solid ${open ? colors.primary : colors.gray200}`, borderRadius: radius.sm,
          background: colors.white, color: sel ? colors.gray900 : colors.gray600,
          fontSize: typography.sizes.base, fontFamily: typography.fontFamily,
          cursor: disabled ? "default" : "pointer", textAlign: "left",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <FaChevronDown size={11} color={colors.gray400} style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
            boxShadow: "0 8px 24px rgba(0,0,0,0.14)", zIndex: 1300,
            maxHeight: 280, overflowY: "auto", WebkitOverflowScrolling: "touch",
          }}
        >
          {options.map((o) => {
            const active = String(o.value) === String(value);
            return (
              <div
                key={String(o.value)}
                role="option"
                aria-selected={active}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: "11px 12px", fontSize: typography.sizes.base, cursor: "pointer",
                  color: active ? colors.primary : colors.gray800,
                  background: active ? colors.primaryLighter : "transparent",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = colors.gray50; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
